import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Eye, EyeOff } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import supabase from './supabase';
import { logger } from './lib/logger';
import FlowchartBuilder from './FlowchartBuilder';
import MindmapBuilder from './MindmapBuilder';
import ProjectList from './ProjectList';
import { ErrorBoundary } from './ErrorBoundary';
import { useT } from './i18n/index';
import type { SavedFlowchart } from './types';
import type { FlowchartWithAccess } from './types/database.types';


type AppMode = 'flowchart' | 'mindmap';

// Handlers that each builder registers when opening the project list
interface ProjectListHandlers {
  filterMapType?: 'flowchart' | 'mindmap';
  onLoadFlowchart: (f: SavedFlowchart) => void;
  onDeleteFlowchart: (id: string) => void;
  onRestoreVersion?: (versionData: SavedFlowchart, flowchart: FlowchartWithAccess) => void;
  onSetProject?: (projectId: string | null, projectName: string | null) => void;
  currentFlowchartId?: string;
}

async function getServerMainJs(): Promise<string | null> {
  try {
    const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
    const manifest = await res.json();
    const entry = (manifest.entrypoints as string[] | undefined)
      ?.find(e => /main\.[a-f0-9]+\.js$/.test(e));
    return entry ?? null;
  } catch {
    return null;
  }
}

function getCurrentMainJs(): string | null {
  const scripts = Array.from(document.scripts).map(s =>
    s.src.startsWith(window.location.origin)
      ? s.src.slice(window.location.origin.length)
      : s.src
  );
  return scripts.find(s => /\/static\/js\/main\.[a-f0-9]+\.js/.test(s)) ?? null;
}

export default function App() {
  const { t } = useT();
  const [user, setUser] = useState<User | null>(null);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [newPasswordDone, setNewPasswordDone] = useState(false);
  const [showPwd1, setShowPwd1] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [recoveryLinkError, setRecoveryLinkError] = useState('');
  const [appMode, setAppMode] = useState<AppMode>(() => {
    const stored = localStorage.getItem('home-map-type');
    return stored === 'mindmap' ? 'mindmap' : 'flowchart';
  });
  const [showProjectList, setShowProjectList] = useState(false);
  const [versionOutdated, setVersionOutdated] = useState(false);
  const projectListHandlersRef = useRef<ProjectListHandlers | null>(null);
  // Distinguishes a SIGNED_OUT event caused by the user's own "sign out" click
  // (manualSignOutRef set just before that call) from one caused by the Supabase
  // client itself giving up on the session — e.g. an "invalid refresh token" /
  // "refresh token not found" error, which happens when two concurrent sessions
  // for the same account race on the same refresh-token rotation (two tabs, two
  // devices sharing a synced browser profile, ...). Multiple concurrent logins
  // are intentionally allowed in this app, so this isn't treated as an error to
  // prevent — just one to explain, instead of silently dropping back to the
  // login screen with no indication of why.
  const manualSignOutRef = useRef(false);
  const prevUserIdRef = useRef<string | null>(null);
  // Guards against sending the "new access request" admin email more than once per
  // browser tab for the same user — onAuthStateChange can fire multiple times in a row
  // for a single login (INITIAL_SESSION immediately followed by SIGNED_IN, a near-
  // simultaneous TOKEN_REFRESHED, multiple open tabs sharing the same session, ...), and
  // each firing used to run its own independent signup check with no de-duplication.
  const notifiedSignupRef = useRef<Set<string>>(new Set());

  const openProjectList = useCallback((handlers: ProjectListHandlers) => {
    projectListHandlersRef.current = handlers;
    setShowProjectList(true);
  }, []);

  // Version check: reload immediately on mount if outdated; show a banner on tab focus
  useEffect(() => {
    // Remove cache-bust param from URL if present (left by a previous reload)
    if (window.location.search.includes('_v=')) {
      window.history.replaceState(null, '', window.location.pathname);
      sessionStorage.removeItem('version-reloaded');
    }

    let initialCheckDone = false;

    const check = async (isInitial: boolean) => {
      const serverJs = await getServerMainJs();
      if (!serverJs) return;
      const currentJs = getCurrentMainJs();
      if (!currentJs) return;
      const serverFile = serverJs.split('/').pop();
      const currentFile = currentJs.split('/').pop();
      if (serverFile && currentFile && serverFile !== currentFile) {
        if (isInitial && !sessionStorage.getItem('version-reloaded')) {
          // Cache-busting navigation so mobile browsers bypass their cached HTML
          sessionStorage.setItem('version-reloaded', '1');
          window.location.replace(window.location.pathname + '?_v=' + Date.now());
        } else {
          setVersionOutdated(true);
        }
      }
    };

    check(true).then(() => { initialCheckDone = true; });

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && initialCheckDone) {
        check(false);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const checkApproval = useCallback((u: import('@supabase/supabase-js').User) => {
    supabase.from('user_profiles').select('approved').eq('id', u.id).single()
      .then(
        ({ data, error }: any) => {
          if (error) {
            // A transient failure (a network blip on wake-from-sleep, an
            // auth token that was just refreshed but hasn't fully propagated
            // yet, ...) must never be read as "not approved" — that showed
            // the awaiting-approval screen to an already-approved user whose
            // laptop had simply been asleep. Retry instead of concluding
            // anything from a failed check; isApproved is left as whatever it
            // already was (null keeps showing the loading state, true keeps
            // the user in the app with no visible disruption).
            logger.error('Approval check failed, retrying:', error);
            setTimeout(() => checkApproval(u), 2000);
            return;
          }
          setIsApproved(data?.approved ?? false);
        },
        (err: any) => {
          logger.error('Approval check rejected, retrying:', err);
          setTimeout(() => checkApproval(u), 2000);
        }
      );
  }, []);

  // Detect password recovery from URL hash (detectSessionInUrl is disabled in supabase.js)
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (!hash) return;
    const params = new URLSearchParams(hash);

    // Supabase redirects with #error=... when the link is expired or invalid
    if (params.get('error')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setRecoveryLinkError('This password reset link has expired or is invalid. Please request a new one.');
      return;
    }

    if (params.get('type') === 'recovery') {
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      if (accessToken && refreshToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error }) => {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
            if (error) {
              setRecoveryLinkError('This password reset link has expired or is invalid. Please request a new one.');
            } else {
              setShowNewPassword(true);
            }
          });
      }
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      setLoading(false);
      if (u) checkApproval(u);
    }).catch(() => {
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user ?? null;
      if (event === 'SIGNED_OUT' && prevUserIdRef.current && !manualSignOutRef.current) {
        setError(t('sessionExpiredElsewhere'));
      }
      manualSignOutRef.current = false;
      prevUserIdRef.current = u?.id ?? null;
      setUser(u);
      if (event === 'PASSWORD_RECOVERY') {
        setShowNewPassword(true);
        return;
      }
      if (u) {
        checkApproval(u);
        if (u.email) {
          const email = u.email;
          setTimeout(() => {
            // Whether this is a brand-new signup is decided by attempting a plain INSERT
            // (not upsert) and looking at whether it succeeds or hits a conflict — the
            // row's own primary key is the one thing that can't be racy or go stale:
            // either this id already has a row (existing user, INSERT fails with a unique
            // violation) or it doesn't (genuinely new, INSERT succeeds). Two earlier
            // approaches both had real bugs: a separate "does a row already exist?" SELECT
            // could silently run as anon if the auth token wasn't fully attached yet, so
            // RLS hid the real row and it read as "doesn't exist" (confirmed via edge
            // function logs: two notify-admin-signup calls 45ms apart for a user who's
            // existed since July); comparing the session's created_at/last_sign_in_at
            // seemed race-free but is wrong for any user who was provisioned via a flow
            // where those two timestamps start near-identical and who hasn't done a fresh
            // interactive sign-in since — last_sign_in_at then never advances, so every
            // future app open still looks like "brand new" forever. Any INSERT failure
            // (conflict or otherwise) is treated as "not new" — a silent missed
            // notification for a rare genuine signup is a far better failure mode here
            // than another false "new access request" email eroding trust in the feature.
            if (notifiedSignupRef.current.has(u.id)) return;
            supabase.from('user_profiles').insert({ id: u.id, email }).then(
              ({ error }) => {
                if (!error) {
                  notifiedSignupRef.current.add(u.id);
                  supabase.functions.invoke('notify-admin-signup', { body: { email } }).then(() => {}, () => {});
                } else {
                  // Row already exists (or the insert failed for some other reason) —
                  // keep email/updated_at fresh via a normal update either way.
                  supabase.from('user_profiles').update({ email, updated_at: new Date().toISOString() }).eq('id', u.id).then(() => {}, () => {});
                }
              },
              () => {}
            );
          }, 2000);
        }
      } else {
        setIsApproved(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkApproval, t]);

  // Set appMode to the home type stored in user_profiles
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('user_profiles').select('home_map_type').eq('id', user.id).single()
      .then(({ data }) => {
        const t = data?.home_map_type;
        if (t === 'mindmap') { setAppMode('mindmap'); localStorage.setItem('home-map-type', 'mindmap'); }
        else if (t === 'flowchart') { setAppMode('flowchart'); localStorage.setItem('home-map-type', 'flowchart'); }
        else { localStorage.removeItem('home-map-type'); }
      });
  }, [user?.id]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = isLogin
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({ email, password });

      if (error) throw error;

      if (!isLogin && data.user) {
        setError(t('checkEmailConfirm'));
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('authFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    manualSignOutRef.current = true;
    await supabase.auth.signOut();
    setUser(null);
    setIsApproved(null);
    // Without this, the auth form stays in whatever mode it was last left in —
    // if "no account? sign up" was ever clicked once this session, every
    // subsequent login attempt (even for an existing, already-signed-up user)
    // silently calls supabase.auth.signUp() instead of signInWithPassword(),
    // which re-triggers Supabase's own "confirm your signup" email.
    setIsLogin(true);
  };

  if (loading && !user) {
    return (
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center" style={{ height: '100dvh' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (showNewPassword) {
    return (
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4" style={{ height: '100dvh' }}>
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">MindYourPlan</h1>
            <p className="text-gray-600">{newPasswordDone ? 'Password updated' : 'Set a new password'}</p>
          </div>
          {newPasswordDone ? (
            <div className="text-center">
              <p className="text-green-600 mb-4">Your password has been updated successfully.</p>
              <button
                onClick={() => { setShowNewPassword(false); setNewPasswordDone(false); setNewPassword(''); setNewPasswordConfirm(''); }}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg transition"
              >Continue</button>
            </div>
          ) : (
            <>
              {newPasswordError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{newPasswordError}</div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                  <div className="relative">
                    <input
                      type={showPwd1 ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      minLength={6}
                      autoFocus
                    />
                    <button type="button" onClick={() => setShowPwd1(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPwd1 ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                  <div className="relative">
                    <input
                      type={showPwd2 ? 'text' : 'password'}
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent ${
                        newPasswordConfirm.length === 0 ? 'border-gray-300 focus:ring-blue-500' :
                        newPassword === newPasswordConfirm ? 'border-green-400 focus:ring-green-400' :
                        'border-red-400 focus:ring-red-400'
                      }`}
                    />
                    <button type="button" onClick={() => setShowPwd2(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPwd2 ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {newPasswordConfirm.length > 0 && (
                    <p className={`text-xs mt-1 ${newPassword === newPasswordConfirm ? 'text-green-600' : 'text-red-500'}`}>
                      {newPassword === newPasswordConfirm ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                  )}
                </div>
                <button
                  onClick={async () => {
                    if (newPassword.length < 6) { setNewPasswordError('Password must be at least 6 characters.'); return; }
                    if (newPassword !== newPasswordConfirm) { setNewPasswordError('Passwords do not match.'); return; }
                    setNewPasswordError('');
                    const { error } = await supabase.auth.updateUser({ password: newPassword });
                    if (error) { setNewPasswordError(error.message); return; }
                    setNewPasswordDone(true);
                  }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg transition"
                >Set new password</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (user && isApproved === null) {
    return (
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center" style={{ height: '100dvh' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (user && isApproved === false) {
    return (
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4" style={{ height: '100dvh' }}>
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('awaitingApproval')}</h2>
          <p className="text-gray-600 mb-2">{t('awaitingApprovalDesc', { email: user.email ?? '' })}</p>
          <p className="text-gray-500 text-sm mb-6">{t('awaitingApprovalNote')}</p>
          <button
            onClick={handleSignOut}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >{t('signOut')}</button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4" style={{ height: '100dvh' }}>
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">MindYourPlan</h1>
            <p className="text-gray-600">{isLogin ? t('signInToContinue') : t('createYourAccount')}</p>
          </div>

          {recoveryLinkError && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
              {recoveryLinkError}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('emailLabel')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('passwordLabel')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title={showPassword ? t('hidePassword') : t('showPassword')}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {!isLogin && (
                <p className="text-xs text-gray-500 mt-1">{t('minChars')}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? t('loading') : (isLogin ? t('signIn') : t('signUp'))}
            </button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <div>
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-blue-500 hover:text-blue-600 text-sm"
                disabled={loading}
              >
                {isLogin ? t('noAccountSignUp') : t('haveAccountSignIn')}
              </button>
            </div>
            {isLogin && (
              <div>
                {resetSent ? (
                  <p className="text-green-600 text-sm">{t('passwordResetSent')}</p>
                ) : (
                  <button
                    onClick={async () => {
                      if (!email) { setError(t('enterEmailFirst')); return; }
                      setError('');
                      const { error } = await supabase.functions.invoke('send-password-reset', { body: { email } });
                      if (error) { setError(t('passwordResetFailed')); return; }
                      setResetSent(true);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                    disabled={loading}
                  >
                    {t('forgotPassword')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handlers = projectListHandlersRef.current;

  return (
    <>
      <Analytics />
      <div style={{ display: appMode === 'flowchart' ? 'block' : 'none' }}>
        <ErrorBoundary label="Flowchart Builder">
          <FlowchartBuilder
            user={user}
            onSignOut={handleSignOut}
            isActive={appMode === 'flowchart'}
            onSwitchToMindmap={() => setAppMode('mindmap')}
            onOpenProjectList={openProjectList}
          />
        </ErrorBoundary>
      </div>
      <div style={{ display: appMode === 'mindmap' ? 'block' : 'none' }}>
        <ErrorBoundary label="Mindmap Builder">
          <MindmapBuilder
            user={user}
            onSignOut={handleSignOut}
            isActive={appMode === 'mindmap'}
            onSwitchToFlowchart={() => setAppMode('flowchart')}
            onOpenProjectList={openProjectList}
          />
        </ErrorBoundary>
      </div>

      {versionOutdated && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#facc15', color: '#713f12', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '8px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          <span>{t('newVersionAvailable')}</span>
          <button
            style={{ background: '#713f12', color: 'white', fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 5, border: 'none', cursor: 'pointer' }}
            onClick={() => window.location.reload()}
          >{t('reloadNow')}</button>
        </div>
      )}

      {showProjectList && handlers && (
        <ProjectList
          user={user}
          filterMapType={handlers.filterMapType}
          onClose={() => setShowProjectList(false)}
          onLoadFlowchart={(f) => { setShowProjectList(false); handlers.onLoadFlowchart(f); }}
          onDeleteFlowchart={handlers.onDeleteFlowchart}
          onRestoreVersion={handlers.onRestoreVersion}
          onSetProject={handlers.onSetProject}
          currentFlowchartId={handlers.currentFlowchartId}
        />
      )}
    </>
  );
}
